import { reduce } from 'lodash/fp';
import { Duration } from 'luxon';
import { DurationPart, durationUnitByDurationPart } from '../enums';

type WithGet<T, P> = {
    [Property in P as `get${Capitalize<string & Property>}`]: (entity: T) => number;
};

const durationAccessible = <T, P extends DurationPart>(toLuxon: (entity: T) => Duration, parts: readonly P[] = []) => {
    // Luxon's durations sometimes return -0, but we expect to be working with +0, and they're not entirely equal.
    const unsignZero = (value: number) => (Math.abs(value) === 0 ? 0 : value);

    const get =
        (partName: P) =>
        (entity: T): number => {
            const luxonEntity = toLuxon(entity);

            // Luxon doesn't automatically overflow some values if they're above their limit, but we expect them to do so.
            // For example, on PT100M, Luxon would give { minutes: 100 }, but we expect { hours: 1, minutes: 40 }.
            const normalizedDuration = luxonEntity.shiftTo(
                'years',
                'months',
                'days',
                'hours',
                'minutes',
                'seconds',
                'milliseconds'
            );

            // However, we don't expect days to overflow to weeks, but we still expect weeks to be present.
            if (partName === DurationPart.Weeks) {
                return unsignZero(Math.trunc(normalizedDuration.get('days') / 7));
            }

            return unsignZero(normalizedDuration.get(durationUnitByDurationPart[partName]));
        };

    return reduce<P, WithGet<T, P>>(
        (operations, partName) => ({
            ...operations,
            [`get${partName}`]: (entity: T) => get(partName)(entity),
        }),
        {} as WithGet<T, P>,
        parts
    );
};

export default durationAccessible;
